import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MoveTask, NextStepButton } from "../components/plan/TaskSteps";
import { useEffect, useState } from "react";
import { currentSubscription, disablePush, enablePush, pushSupported } from "../lib/push";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowDown, ArrowUp, NotebookPen, Bell, CalendarCheck, GripVertical, ClipboardList, Inbox, Check, ExternalLink, Gavel, Hourglass, ListChecks, Lock, Undo2, MessageCircle, Send, Stamp } from "lucide-react";
import { api, post } from "../lib/api";
import { useSession } from "../lib/session";
import type { Item } from "../lib/work";
import { Card, Empty, fmtDate, Modal, PageHeader, Stat } from "../components/ui";
import { StatusPill, SubProgress } from "../components/work";
import { BriefingCard } from "./insights";
import { OutlookAddinCard } from "../components/OutlookAddin";
import { AgendaCard } from "./personal";
import { MailCard } from "./mail";
import { QuickAdd } from "../components/QuickAdd";
import { CameInCard, OtherUpdates } from "../components/CameIn";
import { DailyNotes } from "../components/DailyNotes";
import { ChaseButton } from "../components/Chase";
import { showNext } from "../components/DoneNext";
import { useWealthAccess, WK } from "../components/wealth/shared";

interface LauncherApp {
  id: string;
  name: string;
  url: string;
  group?: string;
}

function useLauncher() {
  return useQuery({ queryKey: ["launcher"], queryFn: () => api<{ items: LauncherApp[] }>("/api/launcher"), staleTime: 5 * 60_000 });
}

function AppGrid({ items }: { items: LauncherApp[] }) {
  const groups = [...new Set(items.map((a) => a.group ?? "Apps"))];
  return (
    <div className="space-y-5">
      {groups.map((g) => (
        <section key={g}>
          {groups.length > 1 && <h2 className="muted mb-2 text-xs font-semibold uppercase tracking-wide">{g}</h2>}
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {items.filter((a) => (a.group ?? "Apps") === g).map((a) => (
              <li key={a.id}>
                <a href={a.url} className="panel group flex h-full items-center gap-3 p-3 text-sm font-medium transition-colors hover:border-brand-400" target="_blank" rel="noreferrer">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-sm font-semibold text-brand-600">{a.name.charAt(0)}</span>
                  <span className="min-w-0 flex-1 truncate">{a.name}</span>
                  <ExternalLink size={14} className="muted shrink-0 group-hover:text-brand-600" />
                </a>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

interface MyDay {
  overdue: Item[];
  today: Item[];
  upcoming: Item[];
  waiting: Item[];
  pool: Item[];
  inbox: Record<string, number>;
  requests: Array<{ id: string; ref: string; typeName: string; title: string; state: string; workItemId: string; createdAt: string }>;
  followUps: TodayItem[];
  decide: Array<{ kind: "inbox" | "task"; id: string; title: string; reason: string; category: string; priority: string; link: string; dueAt: string | null }>;
}
type TodayItem = Item & { personal?: boolean; nextAction?: string | null; followUpOn?: string | null; decisionNeeded?: boolean; assigneeNames?: string[]; waitingOn?: { external?: string; reason?: string } | null };

const DECIDE_ICON: Record<string, React.ReactNode> = { approval: <Stamp size={15} className="text-brand-600" />, decision: <Gavel size={15} className="text-amber-600" />, request: <MessageCircle size={15} className="text-brand-600" /> };

/** The "Decide" part of "Needs you": approvals, requests and tasks marked "decision needed". */
function DecideList({ items }: { items: MyDay["decide"] }) {
  const [all, setAll] = useState(false);
  return (
    <>
      <ul className="px-2">
        {(all ? items : items.slice(0, 5)).map((d) => (
          <li key={`${d.kind}:${d.id}`}>
            <a href={d.link} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-black/[0.03]">
              {DECIDE_ICON[d.category] ?? <Gavel size={15} />}
              <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{d.title}</span>{d.reason && <span className="muted block truncate text-xs">{d.reason}</span>}</span>
              {d.priority === "P1" && <span className="chip text-xs text-red-700">Urgent</span>}
              {d.dueAt && <span className="muted shrink-0 text-xs">{fmtDate(d.dueAt)}</span>}
            </a>
          </li>
        ))}
      </ul>
      {items.length > 5 && <div className="px-5"><button className="btn btn-ghost btn-sm -ml-2 text-brand-700" onClick={() => setAll((x) => !x)}>{all ? "Show fewer" : `Show all ${items.length}`}</button></div>}
    </>
  );
}

/** A task with a tick box: ticking completes it (same as "Done" on the task). */
function FocusRow({ i, overdue, time, handle, tail, liProps }: { i: TodayItem; overdue?: boolean; time?: string | null; handle?: React.ReactNode; tail?: React.ReactNode; liProps?: React.LiHTMLAttributes<HTMLLIElement> }) {
  const qc = useQueryClient();
  const [done, setDone] = useState(false);
  const complete = async () => {
    setDone(true);
    try {
      await post(`/api/work/items/${i.id}/logs`, { outcome: "done" });
      void qc.invalidateQueries({ queryKey: ["my-day"] });
      void qc.invalidateQueries({ queryKey: ["plan-today"] });
      showNext(i.id, i.title);
    } catch {
      setDone(false);
      window.location.assign(`/work/${i.id}`); // something must be done first (checklist, photo) — open it
    }
  };
  return (
    <li {...liProps} className={`flex items-start gap-3 rounded-lg px-3 py-2 hover:bg-black/[0.03] ${liProps?.className ?? ""}`}>
      {handle}
      <button aria-label={`Complete ${i.title}`} onClick={() => void complete()} className={`mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded border-[1.5px] ${done ? "border-emerald-600 bg-emerald-600 text-white" : "border-[var(--muted)] hover:border-emerald-600"}`}>{done && <Check size={12} />}</button>
      {time !== undefined && <span className="num muted mt-0.5 w-11 shrink-0 text-xs">{time ?? "—"}</span>}
      <Link to="/work/$id" params={{ id: i.id }} className="min-w-0 flex-1">
        <span className={`block text-sm ${done ? "muted line-through" : "font-medium"}`}>{i.title}</span>
        {(i.nextAction || i.spaceName) && <span className="muted block truncate text-xs">{i.nextAction ? `Next: ${i.nextAction}` : i.spaceName}</span>}
      </Link>
      <span className="mt-1"><SubProgress s={i.subtasks} /></span>
      {i.personal && <Lock size={12} className="muted mt-1 shrink-0" aria-label="Private" />}
      {i.priority === "P1" ? <span className="chip shrink-0 text-xs text-red-700">Urgent</span> : i.priority === "P2" ? <span className="chip shrink-0 text-xs">High</span> : null}
      {i.dueAt && <span className={`shrink-0 text-xs ${overdue ? "font-semibold text-red-600" : "muted"}`}>{overdue ? "overdue · " : ""}{fmtDate(i.dueAt)}</span>}
      {tail}
      {!done && <NextStepButton id={i.id} title={i.title} current={i.nextAction} className="btn btn-ghost btn-sm -my-1 shrink-0 !px-1.5" />}
    </li>
  );
}

interface DayPlan { planned: boolean; reminders?: Array<{ id: string; title: string; at: string }>; notes?: Array<{ id: string; title: string; itemId: string }>; items: Array<{ itemId: string; minutes: number; start: string | null; pinned?: boolean; ref: string; title: string; priority: string; done: boolean; waiting?: { on: string | null; until: string } | null; nextAction?: string | null; dueAt: string | null; personal: boolean; subtasks?: { total: number; done: number } | null }> }
const hm = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Malta" });

/** One line under Do today: what's not in the plan, reminders, notes for today's tasks, what's due this week. */
function DayFooter({ unplanned, upcoming, reminders = [], notes = [] }: { unplanned: number; upcoming: number; reminders?: NonNullable<DayPlan["reminders"]>; notes?: NonNullable<DayPlan["notes"]> }) {
  const bits: React.ReactNode[] = [];
  if (unplanned > 0) bits.push(<span key="u" className="text-amber-800">{unplanned} due or overdue not in your plan · <Link to="/plan" className="font-medium text-brand-700 hover:underline">add</Link></span>);
  if (reminders.length) bits.push(<span key="r" className="inline-flex flex-wrap items-center gap-x-1"><Bell size={12} aria-hidden />{reminders.map((r, k) => <span key={r.id}>{k > 0 && "· "}<a href={`/notes?note=${r.id}`} className="text-brand-700 hover:underline">{hm(r.at)} {r.title}</a></span>)}</span>);
  if (notes.length) bits.push(<span key="n" className="inline-flex flex-wrap items-center gap-x-1"><NotebookPen size={12} aria-hidden />{notes.map((n, k) => <span key={n.id}>{k > 0 && "· "}<a href={`/notes?note=${n.id}`} className="text-brand-700 hover:underline">{n.title || "Untitled"}</a></span>)}</span>);
  if (upcoming > 0) bits.push(<Link key="w" to="/work" className="hover:text-brand-700 hover:underline">{upcoming} due in the next 7 days</Link>);
  if (!bits.length) return null;
  return <p className="muted flex flex-wrap gap-x-4 gap-y-1 border-t border-[var(--line)] px-5 py-2.5 text-xs">{bits}</p>;
}

/**
 * "Didn't get to these": the unfinished tasks of today go back to the week's "no day yet" list (next week's from
 * Friday on), so Plan my week offers them again. All ticked; untick the ones you'll still do today.
 */
function RequeueDialog({ tasks, onClose }: { tasks: Array<{ id: string; title: string }>; onClose: () => void }) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Set<string>>(() => new Set(tasks.map((t) => t.id)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const friday = new Date().toLocaleDateString("en-GB", { weekday: "long", timeZone: "Europe/Malta" }) === "Friday";
  const go = async () => {
    setBusy(true); setErr(null);
    try {
      await post("/api/plan/queue", { itemIds: [...picked] });
      for (const k of ["plan-today", "plan-day", "my-day", "plan-week", "plan-sort", "work", "plan-sort-count"]) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <Modal title="Didn't get to these" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!picked.size || busy} onClick={() => void go()}>Back to the queue ({picked.size})</button></>}>
      <p className="muted mb-3 text-sm">They come off today and wait, with no day, in {friday ? "next week's" : "this week's"} list on Plan my week. Deadlines don't change. Untick any you'll still do today.</p>
      <ul className="space-y-1">
        {tasks.map((t) => (
          <li key={t.id}>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-black/[0.03]">
              <input type="checkbox" checked={picked.has(t.id)} onChange={() => toggle(t.id)} />
              <span className="min-w-0 flex-1 truncate">{t.title}</span>
            </label>
          </li>
        ))}
      </ul>
      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
    </Modal>
  );
}

/**
 * Your plan today, as you work through it: To do in your order (drag, or the arrows), Waiting for a reply (your part
 * is done — counts as done today), Done. The order is saved; times you set stay, the rest follow the order.
 */
function PlanToday({ items, notes, reminders, others, upcoming }: { items: DayPlan["items"]; notes: NonNullable<DayPlan["notes"]>; reminders: NonNullable<DayPlan["reminders"]>; others: TodayItem[]; upcoming: number }) {
  const qc = useQueryClient();
  const [order, setOrder] = useState<string[] | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [requeue, setRequeue] = useState(false);
  const byId = new Map(items.map((x) => [x.itemId, x]));
  const todoAll = items.filter((x) => !x.done && !x.waiting);
  const ids = order ? [...order.filter((id) => todoAll.some((x) => x.itemId === id)), ...todoAll.filter((x) => !order.includes(x.itemId)).map((x) => x.itemId)] : todoAll.map((x) => x.itemId);
  const todo = ids.map((id) => byId.get(id)!);
  const waiting = items.filter((x) => !x.done && x.waiting);
  const done = items.filter((x) => x.done);
  const finished = waiting.length + done.length;
  const inPlan = new Set(items.map((x) => x.itemId));
  const extra = others.filter((i) => !inPlan.has(i.id)).length;

  const save = async (next: string[]) => {
    setOrder(next);
    setErr(null);
    try {
      await post("/api/plan/day/order", { itemIds: [...next, ...waiting.map((x) => x.itemId), ...done.map((x) => x.itemId)] });
      await qc.invalidateQueries({ queryKey: ["plan-today"] });
      setOrder(null);
    } catch (e) { setErr((e as Error).message); }
  };
  const shift = (id: string, by: number) => {
    const i = ids.indexOf(id), j = i + by;
    if (i < 0 || j < 0 || j >= ids.length) return;
    const next = [...ids];
    [next[i], next[j]] = [next[j]!, next[i]!];
    void save(next);
  };
  const dropOn = (target: string) => {
    if (!drag || drag === target) return;
    const next = ids.filter((x) => x !== drag);
    next.splice(next.indexOf(target), 0, drag);
    setDrag(null);
    void save(next);
  };
  const row = (x: DayPlan["items"][number]) => ({ id: x.itemId, ref: x.ref, title: x.title, priority: x.priority, dueAt: x.dueAt, personal: x.personal, subtasks: x.subtasks, nextAction: x.nextAction ?? null } as TodayItem);
  const section = (icon: React.ReactNode, text: string) => <div className="muted flex items-center gap-1.5 px-5 pb-1 pt-3 text-xs">{icon}{text}</div>;

  return (
    <Card title="Do today" icon={<CalendarCheck size={16} />} actions={<Link to="/plan" className="btn btn-sm">Re-plan</Link>} flush>
      <div className="flex items-center gap-3 px-5 pb-1">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.06]"><div className="h-full rounded-full bg-emerald-600 transition-all" style={{ width: `${items.length ? Math.round((finished / items.length) * 100) : 0}%` }} /></div>
        <span className="muted shrink-0 text-xs">{finished} of {items.length} done today</span>
      </div>
      {section(<ListChecks size={12} />, todo.length > 1 ? "To do · drag to reorder" : "To do")}
      <ul className="px-2">
        {todo.map((x, k) => (
          <FocusRow
            key={x.itemId} time={x.start ? hm(x.start) : null} i={row(x)}
            liProps={{
              draggable: true,
              onDragStart: (e) => { setDrag(x.itemId); e.dataTransfer.effectAllowed = "move"; },
              onDragEnd: () => setDrag(null),
              onDragOver: (e) => { if (drag) e.preventDefault(); },
              onDrop: (e) => { e.preventDefault(); dropOn(x.itemId); },
              className: drag === x.itemId ? "opacity-50" : "",
            }}
            handle={<GripVertical size={14} className="muted -mx-1.5 mt-0.5 hidden shrink-0 cursor-grab sm:block" aria-hidden />}
            tail={<>
              <span className="-my-1 hidden shrink-0 sm:inline"><MoveTask id={x.itemId} title={x.title} compact /></span>
              {todo.length > 1 && (
              <span className="-my-1 flex shrink-0">
                <button type="button" className="btn btn-ghost btn-sm !px-1" disabled={k === 0} onClick={() => shift(x.itemId, -1)} aria-label={`Move ${x.title} up`}><ArrowUp size={13} /></button>
                <button type="button" className="btn btn-ghost btn-sm !px-1" disabled={k === todo.length - 1} onClick={() => shift(x.itemId, 1)} aria-label={`Move ${x.title} down`}><ArrowDown size={13} /></button>
              </span>
              )}
            </>}
          />
        ))}
        {!todo.length && <li className="px-3 py-2 text-sm text-emerald-700">Nothing left to do today.</li>}
      </ul>
      {todo.length > 0 && (
        <div className="px-5 pt-1">
          <button type="button" className="btn btn-ghost btn-sm muted !px-1.5 text-xs" onClick={() => setRequeue(true)}><Undo2 size={13} /> Didn't get to these · back to the queue</button>
        </div>
      )}
      {requeue && <RequeueDialog tasks={todo.map((x) => ({ id: x.itemId, title: x.title }))} onClose={() => setRequeue(false)} />}
      {err && <p className="px-5 text-xs text-red-600">Couldn't save the order: {err}</p>}
      {waiting.length > 0 && <>
        {section(<Hourglass size={12} />, "Your part is done · waiting for a reply or the next step")}
        <ul className="px-2">
          {waiting.map((x) => (
            <li key={x.itemId} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-black/[0.03]">
              <Hourglass size={15} className="shrink-0 text-amber-600" aria-hidden />
              <Link to="/work/$id" params={{ id: x.itemId }} className="min-w-0 flex-1">
                <span className="block truncate text-sm">{x.title}</span>
                <span className="muted block truncate text-xs">{x.waiting!.on ? `Waiting on ${x.waiting!.on} · check back` : `Next${x.nextAction ? `: ${x.nextAction}` : " step"} ·`} {new Date(`${x.waiting!.until}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</span>
              </Link>
              {x.waiting!.on ? (
                <>
                  <span className="chip shrink-0 bg-amber-50 text-xs text-amber-800">Waiting</span>
                  <NextStepButton id={x.itemId} title={x.title} current={x.nextAction} replyCame label="Reply came" className="btn btn-sm shrink-0" />
                </>
              ) : <span className="chip shrink-0 text-xs">Step done</span>}
            </li>
          ))}
        </ul>
      </>}
      {/* finished tasks leave Today (Liam, 23 Oct); the bar above still counts them */}
      <div className="h-2" />
      <DayFooter unplanned={extra} upcoming={upcoming} reminders={reminders} notes={notes} />
    </Card>
  );
}

/** Do today: your plan once you've made it; before that, what's due and overdue with a nudge to plan. */
function FocusToday({ overdue, today, upcoming, plan }: { overdue: TodayItem[]; today: TodayItem[]; upcoming: number; plan?: DayPlan }) {
  const [all, setAll] = useState(false);
  if (plan?.planned) return <PlanToday items={plan.items} notes={plan.notes ?? []} reminders={plan.reminders ?? []} others={[...overdue, ...today]} upcoming={upcoming} />;
  const rows = [...overdue.map((i) => ({ i, overdue: true })), ...today.map((i) => ({ i, overdue: false }))];
  const shown = all ? rows : rows.slice(0, 10);
  return (
    <Card title="Do today" icon={<CalendarCheck size={16} />} flush>
      <p className="muted px-5 pb-2 text-xs">Not planned yet. Below is what's due or overdue — <Link to="/plan" className="font-medium text-brand-700 hover:underline">Plan my day</Link> to choose what you'll really do and fit it around your meetings.</p>
      {rows.length ? (
        <>
          <ul className="px-2 pb-1">{shown.map((r) => <FocusRow key={r.i.id} i={r.i} overdue={r.overdue} />)}</ul>
          {rows.length > 10 && <div className="px-5 pb-2"><button className="btn btn-ghost btn-sm -ml-2 text-brand-700" onClick={() => setAll((x) => !x)}>{all ? "Show fewer" : `Show all ${rows.length}`}</button></div>}
        </>
      ) : <p className="muted px-5 pb-3 text-sm">Nothing due today.</p>}
      <DayFooter unplanned={0} upcoming={upcoming} reminders={plan?.reminders} notes={plan?.notes} />
    </Card>
  );
}

/** Waiting on others and follow-ups due today, in one list: follow-ups first, then everything you're waiting on. */
function WaitingCard({ waiting, followUps, hide }: { waiting: TodayItem[]; followUps: TodayItem[]; hide: Set<string> }) {
  const due = new Set(followUps.map((i) => i.id));
  const items = [...followUps, ...waiting.filter((i) => !due.has(i.id))].filter((i) => !hide.has(i.id));
  return (
    <PeopleList chase title="Waiting on others" icon={<Hourglass size={16} />} items={items}
      who={(i) => {
        const on = i.waitingOn?.external ? `Waiting on ${i.waitingOn.external}` : i.assigneeNames?.length ? `With ${i.assigneeNames.join(", ")}` : i.nextAction ? `Next: ${i.nextAction}` : "Waiting";
        // a check-back date already passed is a missed follow-up, not "today" (it also shows in the weekly review)
        const today = new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Malta" });
        if (due.has(i.id) && i.followUpOn && i.followUpOn.slice(0, 10) < today) return `Follow-up missed · ${new Date(`${i.followUpOn.slice(0, 10)}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · ${on}`;
        return due.has(i.id) ? `Follow up today · ${on}` : on;
      }} />
  );
}

function RequestsCard({ requests }: { requests: MyDay["requests"] }) {
  const [open, setOpen] = useState(false);
  if (!requests.length) return null;
  return (
    <Card title="Your requests" icon={<Send size={16} />} count={requests.length} actions={<button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen((x) => !x)}>{open ? "Hide" : "Show"}</button>}>
      {open ? (
        <ul className="-mx-1 space-y-1">
          {requests.map((r) => (
            <li key={r.id}>
              <Link to="/work/$id" params={{ id: r.workItemId }} className="block rounded-lg px-2 py-2 hover:bg-black/[0.03]">
                <div className="text-sm font-semibold">{r.title}</div>
                <div className="muted mt-1 flex flex-wrap items-center gap-2 text-xs"><span className="num">{r.ref}</span><StatusPill state={r.state} /><span>{fmtDate(r.createdAt)}</span></div>
              </Link>
            </li>
          ))}
        </ul>
      ) : <p className="muted text-xs">What you asked others for, and where each one stands.</p>}
    </Card>
  );
}

function PeopleList({ title, icon, items, empty, who, chase }: { title: string; icon: React.ReactNode; items: TodayItem[]; empty?: string; who: (i: TodayItem) => string; chase?: boolean }) {
  if (!items.length && !empty) return null;
  return (
    <Card title={title} icon={icon} count={items.length} flush>
      {items.length ? (
        <ul className="px-2 pb-1">
          {items.slice(0, 8).map((i) => (
            <li key={i.id} className="flex items-start gap-2 rounded-lg px-3 py-2 hover:bg-black/[0.03]">
              <Link to="/work/$id" params={{ id: i.id }} className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{i.title}</span>
                <span className="muted block truncate text-xs">{who(i)}{chase && (i as TodayItem & { chaseCount?: number }).chaseCount ? ` · chased ${(i as TodayItem & { chaseCount?: number }).chaseCount}×` : ""}</span>
              </Link>
              {chase && <ChaseButton item={i as never} iconOnly compact />}
            </li>
          ))}
        </ul>
      ) : <p className="muted px-5 pb-3 text-sm">{empty}</p>}
    </Card>
  );
}


const INBOX_LABEL: Record<string, string> = { approval: "approvals", decision: "decisions", mention: "mentions", escalation: "escalations", task: "tasks", request: "requests", waiting: "waiting on", update: "updates" };

function usePushNudge() {
  const [state, setState] = useState<"unknown" | "on" | "off" | "denied" | "unavailable">("unknown");
  useEffect(() => {
    if (!pushSupported()) return setState("unavailable");
    if (Notification.permission === "denied") return setState("denied");
    void currentSubscription().then((s) => setState(s ? "on" : "off"));
  }, []);
  if (state === "on" || state === "unknown" || state === "unavailable") return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-100 bg-brand-50/60 px-4 py-2 text-sm">
      <span className="flex items-center gap-2.5 font-medium text-[var(--text-2)]">
        <Bell size={16} className="text-brand-600" />
        {state === "denied" ? "Notifications are blocked for ONE in this browser's settings." : "Get a notification on this device when something needs you."}
      </span>
      {state === "off" && <button className="btn btn-primary btn-sm" onClick={() => void enablePush().then((r) => setState(r === "enabled" ? "on" : r))}>Turn on notifications</button>}
    </div>
  );
}
void disablePush;


/** Friday–Tuesday, until this week's review is done. */
function useReviewNudge() {
  const r = useQuery({ queryKey: ["review-status"], queryFn: () => api<{ due: boolean; weekStart: string }>("/api/review/status"), staleTime: 300_000 });
  if (!r.data?.due) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm">
      <CalendarCheck size={16} className="shrink-0 text-amber-700" />
      <span className="min-w-0 flex-1"><b>Weekly review.</b> Ten minutes to close the week and choose next week's focus.</span>
      <Link to="/review" className="btn btn-sm btn-primary">Start</Link>
    </div>
  );
}

/** Yearly wealth review (private): from the day it is due until it is marked done. */
function useWealthNudge() {
  const enabled = useWealthAccess().data?.enabled === true;
  const r = useQuery({ queryKey: WK.reviewStatus, queryFn: () => api<{ due: boolean }>("/api/wealth/review/status"), enabled, staleTime: 30 * 60_000, refetchInterval: 30 * 60_000, retry: false });
  if (!enabled || !r.data?.due) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm">
      <ClipboardList size={16} className="shrink-0 text-amber-700" />
      <span className="min-w-0 flex-1"><b>Yearly wealth review is due.</b> The pack is ready: what changed, where you stand against your policy, and the questions to decide.</span>
      <Link to="/wealth" search={{ tab: "strategy", review: 1 } as never} className="btn btn-sm btn-primary">Open the review</Link>
    </div>
  );
}

/** One slim strip at a time: weekly review, then wealth review, then turning on notifications. */
function Nudges() {
  const review = useReviewNudge();
  const wealth = useWealthNudge();
  const push = usePushNudge();
  return review ?? wealth ?? push;
}

/** "Sort tasks", with how many tasks still have no "when" (Plan step 1). */
function SortButton() {
  const s = useQuery({ queryKey: ["plan-sort-count"], queryFn: () => api<{ counts: { undecided: number } }>("/api/plan/sort?view=undecided&limit=1"), staleTime: 60_000 });
  const n = s.data?.counts.undecided ?? 0;
  return <Link to="/plan/sort" className="btn btn-sm" aria-label={n > 0 ? `Sort tasks, ${n} to sort` : "Sort tasks"}><ListChecks size={15} /> Sort tasks{n > 0 && <span className="chip text-xs num">{n}</span>}</Link>;
}

/** "Triage · N" when there are tasks still to be processed. */
function TriageButton() {
  const t = useQuery({ queryKey: ["triage"], queryFn: () => api<{ count: number }>("/api/tasks/triage"), staleTime: 60_000 });
  if (!t.data?.count) return null;
  return <Link to="/triage" className="btn btn-sm"><Inbox size={15} /> Triage <span className="chip text-xs">{t.data.count}</span></Link>;
}

export function MyDayPage() {
  const s = useSession();
  const day = useQuery({ queryKey: ["my-day"], queryFn: () => api<MyDay>("/api/my-day"), refetchInterval: 60_000 });
  const first = s.person?.name.split(" ")[0] ?? "";
  const d = day.data;
  const plan = useQuery({ queryKey: ["plan-today"], queryFn: () => api<DayPlan>("/api/plan/today"), refetchInterval: 60_000 });
  // tasks already in Do today aren't repeated under From staff (the app boxes always list every open request)
  const shown = new Set<string>([
    ...(plan.data?.planned ? plan.data.items.map((x) => x.itemId) : [...(d?.overdue ?? []), ...(d?.today ?? [])].map((x) => x.id)),
  ]);
  const weekAhead = Date.now() + 7 * 86_400_000;
  const dueSoon = (d?.upcoming ?? []).filter((x) => x.dueAt && Date.parse(x.dueAt) < weekAhead).length;
  const planWaiting = new Set(plan.data?.planned ? plan.data.items.filter((x) => x.waiting).map((x) => x.itemId) : []);
  return (
    <div className="space-y-5">
      <PageHeader eyebrow={new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} title={`${greeting()}, ${first}`} actions={<span className="flex items-center gap-2"><Link to="/plan" className="btn btn-sm btn-primary"><CalendarCheck size={15} /> Plan my day</Link><SortButton /><TriageButton /></span>} />
      <QuickAdd />
      <Nudges />
      {day.isLoading ? <Empty>Loading your day…</Empty> : d && (
        <>
          <div className="grid gap-5 xl:grid-cols-3">
            <div className="space-y-5 xl:col-span-2">
              <FocusToday overdue={d.overdue as TodayItem[]} today={d.today as TodayItem[]} upcoming={dueSoon} plan={plan.data} />
              <CameInCard hide={shown} decide={d.decide ?? []} renderDecide={(items) => <DecideList items={items} />} />
            </div>
            <div className="space-y-5">
              <AgendaCard />
              <DailyNotes />
              <WaitingCard waiting={d.waiting as TodayItem[]} followUps={d.followUps ?? []} hide={planWaiting} />
              <MailCard />
              <RequestsCard requests={d.requests} />
              <BriefingCard />
            </div>
          </div>
          <OtherUpdates />
        </>
      )}
    </div>
  );
}

export function AppsPage() {
  const launcher = useLauncher();
  return (
    <div>
      <PageHeader title="Apps" subtitle="Every Infinite Fusion and Mythos app you can open — one login for all of them." />
      {launcher.isLoading ? <Empty>Loading…</Empty> : launcher.data?.items.length ? <AppGrid items={launcher.data.items} /> : <Empty>No apps granted yet.</Empty>}
      <h2 className="mb-3 mt-8 text-lg font-semibold">Downloads</h2>
      <OutlookAddinCard />
    </div>
  );
}
